import React, { FC, ReactNode, useContext, useMemo, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import {
	LibraryBooksOutlined as LibraryBooksOutlinedIcon,
	PlaylistRemoveOutlined as PlaylistRemoveOutlinedIcon,
	RateReviewOutlined as RateReviewOutlinedIcon,
	ReportProblemOutlined as ReportProblemOutlinedIcon,
} from "@mui/icons-material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import NorthIcon from "@mui/icons-material/North";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SearchIcon from "@mui/icons-material/Search";
import SouthIcon from "@mui/icons-material/South";
import {
	Box,
	Chip,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Skeleton,
	Stack,
	Tab,
	Tabs,
	TextField,
	Tooltip,
	Typography,
	useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import ReactECharts from "echarts-for-react";

import { SATOSLink } from "@/components/Application/SATOSLink";

import { Procedure } from "@/model/Procedure/Procedure";
import { UserContext } from "@/model/User/UserContext";

import {
	ProcedureQueryKeys,
	useFetchActiveProcedureGroupFolders,
	useFetchProcedures,
} from "@/service/ProcedureQueries";
import { useFetchProcedureUpdateSuggestions } from "@/service/TaskQueries";

dayjs.extend(relativeTime);

/* ────────────────────────────────────────────────────────── */
/* Reusable, aligned KPI card                                 */
/* ────────────────────────────────────────────────────────── */

const KPI_HEIGHT = 88;

const KpiStat: FC<{
	label: ReactNode;
	value: ReactNode;
	icon: ReactNode;
	color?: "primary" | "success" | "warning" | "info" | "error" | "secondary";
	hint?: ReactNode;
	onClick?: () => void;
}> = ({ label, value, icon, color = "primary", hint, onClick }) => (
	<Paper
		variant="outlined"
		onClick={onClick}
		sx={(t) => ({
			p: 1.75,
			borderRadius: 2,
			display: "grid",
			gridTemplateColumns: "44px 1fr",
			gridTemplateRows: "auto auto",
			columnGap: 1.25,
			rowGap: 0.25,
			alignItems: "center",
			minHeight: KPI_HEIGHT,
			cursor: onClick ? "pointer" : "default",
			position: "relative",
			overflow: "hidden",
			"&:before": {
				content: '""',
				position: "absolute",
				left: 0,
				right: 0,
				top: 0,
				height: 4,
				background: `linear-gradient(90deg, ${alpha(
					t.palette[color].main,
					0.65,
				)}, ${alpha(t.palette[color].light, 0.65)})`,
			},
			transition: "box-shadow .18s ease, transform .18s ease",
			"&:hover": onClick
				? { boxShadow: 3, transform: "translateY(-1px)" }
				: undefined,
		})}
	>
		<Box
			sx={(t) => ({
				width: 44,
				height: 44,
				borderRadius: 2,
				display: "grid",
				placeItems: "center",
				background: `linear-gradient(135deg, ${alpha(
					t.palette[color].light,
					0.35,
				)}, ${alpha(t.palette[color].main, 0.35)})`,
				border: `1px solid ${alpha(t.palette[color].main, 0.25)}`,
				boxShadow: `inset 0 1px 0 ${alpha("#fff", 0.5)}`,
				color: t.palette[color].main,
				gridRow: "1 / span 2",
			})}
		>
			{icon}
		</Box>

		<Typography
			variant="caption"
			color="text.secondary"
			sx={{ fontWeight: 700, letterSpacing: 0.3, alignSelf: "end" }}
		>
			{label}
		</Typography>

		<Typography
			variant="h5"
			sx={{ fontWeight: 900, lineHeight: 1.1, alignSelf: "start" }}
		>
			{value}
		</Typography>

		{hint ? (
			<Typography
				variant="caption"
				color="text.secondary"
				noWrap
				sx={{ gridColumn: "2", mt: -0.25 }}
			>
				{hint}
			</Typography>
		) : null}
	</Paper>
);

/* ────────────────────────────────────────────────────────── */
/* helpers                                                    */
/* ────────────────────────────────────────────────────────── */

const fmtDate = (iso?: string | null) =>
	iso ? dayjs(iso).format("DD/MM/YY HH:mm") : "—";
const isWithinDays = (iso?: string | null, days = 7) =>
	!!iso && dayjs().diff(dayjs(iso), "day") <= days;
const statusLabel = (n?: number) =>
	n === 0
		? "Draft"
		: n === 1
			? "In Review"
			: n === 2
				? "Active"
				: n === 3
					? "Archived"
					: "Unknown";
type SortBy =
	| "name"
	| "updated"
	| "suggestions"
	| "unverified"
	| "tasks"
	| "group"
	| "status"
	| "id";
type SortDir = "asc" | "desc";
const normalizeSugStatus = (s?: string | null): string => {
	if (!s) return "Unknown";
	const low = String(s).trim().toLowerCase();
	if (low === "unverified") return "Unverified";
	if (low === "closed") return "Closed";
	if (low === "open") return "Open";
	if (low === "declined") return "Declined";
	if (low === "approved") return "Approved";
	return s.trim();
};

/* ────────────────────────────────────────────────────────── */
/* main                                                       */
/* ────────────────────────────────────────────────────────── */

const ProceduresOverview: FC<{
	initialRangeDays?: 90 | 180 | 365 | 730;
	height?: number;
}> = ({ initialRangeDays = 365, height = 520 }) => {
	const theme = useTheme();
	const user = useContext(UserContext); // not used now, but kept if needed later

	/* data */
	const { isLoading: isProceduresLoading, data: procedures = [] } =
		useFetchProcedures(ProcedureQueryKeys.filterFolderEqCurrentOrderByNameAsc);
	const { groupList } = useFetchActiveProcedureGroupFolders();
	const { data: suggestionsData, isLoading: isLoadingSuggestions } =
		useFetchProcedureUpdateSuggestions(false);

	/* state */
	const [tab, setTab] = useState<0 | 1>(0);
	const [query, setQuery] = useState("");
	const [onlyQuarter, setOnlyQuarter] = useState(false); // last 90d
	const [hasSuggestions, setHasSuggestions] = useState(false);
	const [sortBy, setSortBy] = useState<SortBy>("name");
	const [sortDir, setSortDir] = useState<SortDir>("asc");

	// Time range for the "updates over time" chart
	const [rangeDays, setRangeDays] = useState<number>(initialRangeDays);
	const RANGE_CHOICES = [90, 180, 365, 730] as const;
	const RANGE_LABELS: Record<number, string> = {
		90: "3 mo",
		180: "6 mo",
		365: "1 yr",
		730: "2 yr",
	};
	const rangeLabel = RANGE_LABELS[rangeDays] ?? `${rangeDays}d`;

	/* lookups */
	const groupNameById = useMemo(() => {
		const map = new Map<number, string>();
		(groupList ?? []).forEach((g) => map.set(g.Id, g.Name));
		return map;
	}, [groupList]);

	/* suggestions keyed by ProcedureId */
	type SugCounts = {
		total: number;
		unverified: number;
		byStatus: Map<string, number>;
	};
	const suggestionCountsByProcedure = useMemo(() => {
		const map = new Map<number, SugCounts>();
		if (!Array.isArray(suggestionsData)) return map;

		suggestionsData.forEach((proc: any) => {
			const pid: number = proc?.ProcedureId;
			if (!pid) return;
			let total = 0;
			let unverified = 0;
			const byStatus = new Map<string, number>();

			proc.Checklists?.forEach((cl: any) => {
				cl?.ProcedureUpdateSuggestions?.forEach((sug: any) => {
					total++;
					const norm = normalizeSugStatus(sug?.Status);
					byStatus.set(norm, (byStatus.get(norm) ?? 0) + 1);
					if (norm === "Unverified") unverified++;
				});
			});

			if (total > 0) map.set(pid, { total, unverified, byStatus });
		});

		return map;
	}, [suggestionsData]);

	const suggestionsStatusTotals = useMemo(() => {
		const all = new Map<string, number>();
		suggestionCountsByProcedure.forEach((c) =>
			c.byStatus.forEach((v, k) => all.set(k, (all.get(k) ?? 0) + v)),
		);
		return Array.from(all.entries()).map(([name, value]) => ({ name, value }));
	}, [suggestionCountsByProcedure]);

	const totalSuggestions = useMemo(() => {
		let sum = 0;
		suggestionCountsByProcedure.forEach((c) => (sum += c.total));
		return sum;
	}, [suggestionCountsByProcedure]);

	const totalUnverified = useMemo(() => {
		let sum = 0;
		suggestionCountsByProcedure.forEach((c) => (sum += c.unverified));
		return sum;
	}, [suggestionCountsByProcedure]);

	/* filters */
	const filtered = useMemo(() => {
		let list = procedures as Procedure[];

		if (query.trim()) {
			const q = query.trim().toLowerCase();
			list = list.filter(
				(p) => p.Name?.toLowerCase().includes(q) || String(p.Id).includes(q),
			);
		}
		if (onlyQuarter) list = list.filter((p) => isWithinDays(p.Updated, 90));

		if (hasSuggestions && !isLoadingSuggestions) {
			list = list.filter(
				(p) => (suggestionCountsByProcedure.get(p.Id)?.total ?? 0) > 0,
			);
		}

		return list;
	}, [
		procedures,
		query,
		onlyQuarter,
		hasSuggestions,
		isLoadingSuggestions,
		suggestionCountsByProcedure,
	]);

	/* sorting */
	const sorted = useMemo(() => {
		const dir = sortDir === "asc" ? 1 : -1;
		const statusOrder = (n?: number) => (typeof n === "number" ? n : 999);

		return [...filtered].sort((a, b) => {
			const aSug = suggestionCountsByProcedure.get(a.Id);
			const bSug = suggestionCountsByProcedure.get(b.Id);
			const aSugTotal = aSug?.total ?? 0;
			const bSugTotal = bSug?.total ?? 0;
			const aUnv = aSug?.unverified ?? 0;
			const bUnv = bSug?.unverified ?? 0;

			const aName = (a.Name || "").toLowerCase();
			const bName = (b.Name || "").toLowerCase();
			const aUpd = dayjs(a.Updated || 0).valueOf();
			const bUpd = dayjs(b.Updated || 0).valueOf();
			const aGroup = (
				groupNameById.get(a.GroupId) ?? "Ungrouped"
			).toLowerCase();
			const bGroup = (
				groupNameById.get(b.GroupId) ?? "Ungrouped"
			).toLowerCase();

			switch (sortBy) {
				case "name":
					return aName < bName ? -1 * dir : aName > bName ? 1 * dir : 0;
				case "updated":
					return (aUpd - bUpd) * dir;
				case "suggestions":
					return (aSugTotal - bSugTotal) * dir;
				case "unverified":
					return (aUnv - bUnv) * dir;
				case "tasks":
					return ((a.TotalTasks ?? 0) - (b.TotalTasks ?? 0)) * dir;
				case "group":
					return aGroup < bGroup ? -1 * dir : aGroup > bGroup ? 1 * dir : 0;
				case "status":
					return (
						(statusOrder(a.ProcedureStatusNum) -
							statusOrder(b.ProcedureStatusNum)) *
						dir
					);
				case "id":
					return (a.Id - b.Id) * dir;
				default:
					return 0;
			}
		});
	}, [filtered, sortBy, sortDir, groupNameById, suggestionCountsByProcedure]);

	/* KPIs */
	const kpis = useMemo(() => {
		const total = procedures.length;
		const recent = procedures.filter((p) => isWithinDays(p.Updated, 7)).length;
		const withNoTasks = procedures.filter(
			(p) => (p.TotalTasks ?? 0) === 0,
		).length;
		const active = procedures.filter((p) => p.ProcedureStatusNum === 2).length;
		return { total, recent, withNoTasks, active };
	}, [procedures]);

	/* trend (rangeDays window) */
	const byDay = useMemo(() => {
		const now = dayjs();
		const start = now.subtract(rangeDays, "day").startOf("day");
		const buckets: Record<string, number> = {};
		for (let i = 0; i <= rangeDays; i++) {
			const d = start.add(i, "day").format("DD/MM/YY");
			buckets[d] = 0;
		}
		procedures.forEach((p) => {
			if (!p.Updated) return;
			const d = dayjs(p.Updated);
			if (d.isBefore(start)) return;
			const key = d.format("DD/MM/YY");
			if (key in buckets) buckets[key] += 1;
		});
		const labels = Object.keys(buckets);
		const values = labels.map((l) => buckets[l]);
		return { labels, values };
	}, [procedures, rangeDays]);

	/* charts */
	const byGroup = useMemo(() => {
		const counts: Record<string, number> = {};
		procedures.forEach((p) => {
			const name = groupNameById.get(p.GroupId) ?? "Ungrouped";
			counts[name] = (counts[name] ?? 0) + 1;
		});
		const entries = Object.entries(counts)
			.sort((a, b) => b[1] - a[1])
			.slice(0, 12);
		return {
			labels: entries.map(([n]) => n),
			values: entries.map(([, v]) => v),
		};
	}, [procedures, groupNameById]);

	const topSuggestionProcedures = useMemo(() => {
		const entries = procedures
			.map((p) => ({
				name: p.Name,
				id: p.Id,
				total: suggestionCountsByProcedure.get(p.Id)?.total ?? 0,
			}))
			.filter((x) => x.total > 0)
			.sort((a, b) => b.total - a.total)
			.slice(0, 12);

		return {
			labels: entries.map((e) => e.name),
			values: entries.map((e) => e.total),
			ids: entries.map((e) => e.id),
		};
	}, [procedures, suggestionCountsByProcedure]);

	/* echarts options */
	const optByGroup = useMemo(
		() => ({
			grid: { left: 12, right: 12, top: 24, bottom: 24, containLabel: true },
			xAxis: {
				type: "value",
				axisLabel: { color: theme.palette.text.secondary },
			},
			yAxis: {
				type: "category",
				data: byGroup.labels,
				axisLabel: { color: theme.palette.text.secondary },
			},
			series: [
				{ type: "bar", data: byGroup.values, itemStyle: { borderRadius: 4 } },
			],
			tooltip: { trigger: "axis" },
		}),
		[byGroup, theme.palette.text.secondary],
	);

	// Decide the best target (tab + checklist + specific suggestion) for a given procedure
	type BestTarget = {
		tab: "newImprov" | "myImprov" | "verified" | "declined";
		checklistId: number;
		suggestionId?: number;
		stepId?: number;
	};

	const findBestSuggestionTarget = React.useCallback(
		(procId: number): BestTarget | undefined => {
			const proc = (suggestionsData ?? []).find(
				(x: any) => x?.ProcedureId === procId,
			);
			if (!proc) return;

			// Flatten all suggestions with their parent checklist id
			const all: Array<any & { checklistId: number }> = [];
			proc.Checklists?.forEach((cl: any) =>
				cl?.ProcedureUpdateSuggestions?.forEach((s: any) =>
					all.push({ ...s, checklistId: cl.ChecklistId }),
				),
			);
			if (!all.length) return;

			const norm = (s: any) => String(s?.Status || "").toLowerCase();
			const by = (status: string) => all.filter((s) => norm(s) === status);
			const newest = (arr: any[]) =>
				arr
					.slice()
					.sort(
						(a, b) => dayjs(b.Created).valueOf() - dayjs(a.Created).valueOf(),
					)[0];

			// Priority: Unverified -> Verified -> Declined -> Mine -> Anything
			const unv = by("unverified");
			if (unv.length) {
				const s = newest(unv);
				return {
					tab: "newImprov",
					checklistId: s.checklistId,
					suggestionId: s.Id,
					stepId: s.StepId,
				};
			}
			const ver = by("verified");
			if (ver.length) {
				const s = newest(ver);
				return {
					tab: "verified",
					checklistId: s.checklistId,
					suggestionId: s.Id,
					stepId: s.StepId,
				};
			}
			const dec = by("declined");
			if (dec.length) {
				const s = newest(dec);
				return {
					tab: "declined",
					checklistId: s.checklistId,
					suggestionId: s.Id,
					stepId: s.StepId,
				};
			}
			const mine = all.filter((s) => s?.CreatedBy?.Id === user?.Id);
			if (mine.length) {
				const s = newest(mine);
				return {
					tab: "myImprov",
					checklistId: s.checklistId,
					suggestionId: s.Id,
					stepId: s.StepId,
				};
			}
			const s = newest(all);
			return {
				tab: "newImprov",
				checklistId: s.checklistId,
				suggestionId: s.Id,
				stepId: s.StepId,
			};
		},
		[suggestionsData, user?.Id],
	);

	// Persist selection so the destination page autoselects/highlights
	const seedSuggestionSelection = (t: BestTarget, procedureId: number) => {
		try {
			localStorage.setItem(
				`suggestionSelection_${t.tab}`,
				JSON.stringify({
					id: procedureId, // procedure id
					checklistId: t.checklistId, // checklist id
					stepId: t.stepId, // optional
					suggestionId: t.suggestionId, // optional
				}),
			);
		} catch {
			// ignore
		}
	};

	const optSuggestionsPie = useMemo(
		() => ({
			tooltip: { trigger: "item" },
			series: [
				{
					type: "pie",
					radius: ["45%", "75%"],
					padAngle: 1,
					itemStyle: { borderRadius: 6 },
					label: { show: true, formatter: "{b}\n{c}" },
					data: suggestionsStatusTotals.length
						? suggestionsStatusTotals
						: [{ name: "No suggestions", value: 1 }],
				},
			],
		}),
		[suggestionsStatusTotals],
	);

	const optTopSug = useMemo(
		() => ({
			grid: { left: 12, right: 12, top: 24, bottom: 24, containLabel: true },
			xAxis: {
				type: "value",
				axisLabel: { color: theme.palette.text.secondary },
			},
			yAxis: {
				type: "category",
				data: topSuggestionProcedures.labels,
				axisLabel: { color: theme.palette.text.secondary },
			},
			series: [
				{
					type: "bar",
					data: topSuggestionProcedures.values,
					itemStyle: { borderRadius: 4 },
				},
			],
			tooltip: { trigger: "axis" },
		}),
		[topSuggestionProcedures, theme.palette.text.secondary],
	);

	const optByDay = useMemo(
		() => ({
			grid: { left: 12, right: 12, top: 24, bottom: 24, containLabel: true },
			xAxis: {
				type: "category",
				data: byDay.labels,
				axisLabel: { color: theme.palette.text.secondary },
			},
			yAxis: {
				type: "value",
				axisLabel: { color: theme.palette.text.secondary },
			},
			series: [
				{
					type: "line",
					smooth: true,
					showSymbol: false,
					data: byDay.values,
					areaStyle: {},
				},
			],
			tooltip: { trigger: "axis" },
		}),
		[byDay, theme.palette.text.secondary],
	);

	/* skeleton */
	const LoadingSkeleton = (
		<Grid container spacing={2} alignItems="stretch">
			<Grid item xs={12}>
				<Skeleton variant="rounded" height={84} />
			</Grid>
			<Grid item xs={12} md={6}>
				<Skeleton variant="rounded" height={360} />
			</Grid>
			<Grid item xs={12} md={6}>
				<Skeleton variant="rounded" height={360} />
			</Grid>
			<Grid item xs={12}>
				<Skeleton variant="rounded" height={320} />
			</Grid>
		</Grid>
	);

	if (isProceduresLoading) return LoadingSkeleton;

	return (
		<Stack spacing={2}>
			{/* FILTERS — full width, aligned */}
			<Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
				<Stack
					direction={{ xs: "column", md: "row" }}
					spacing={1.25}
					alignItems="center"
				>
					<TextField
						size="small"
						placeholder="Search procedures by name or ID…"
						value={query}
						onChange={(e) => setQuery(e.target.value)}
						sx={{ flex: 1 }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" />
								</InputAdornment>
							),
						}}
					/>

					<Stack
						direction="row"
						spacing={1}
						flexWrap="wrap"
						sx={{ alignItems: "center" }}
					>
						<Chip
							variant={onlyQuarter ? "filled" : "outlined"}
							color={onlyQuarter ? "primary" : "default"}
							onClick={() => setOnlyQuarter((v) => !v)}
							label="Updated last quarter"
							sx={{ borderRadius: 2 }}
						/>
						<Chip
							variant={hasSuggestions ? "filled" : "outlined"}
							color={hasSuggestions ? "primary" : "default"}
							onClick={() => setHasSuggestions((v) => !v)}
							label="Has suggestions"
							disabled={isLoadingSuggestions}
							sx={{ borderRadius: 2 }}
						/>
					</Stack>
				</Stack>
			</Paper>

			{/* CONTENT — left charts / right list */}
			<Grid container spacing={2} alignItems="stretch">
				{/* LEFT */}
				<Grid item xs={12} md={5} lg={6}>
					<Stack spacing={2} sx={{ height: "100%" }}>
						<Grid container spacing={2} alignItems="stretch" sx={{ pr: 2 }}>
							<Grid item xs={12} sm={6} md={3}>
								<KpiStat
									label="Total procedures"
									value={kpis.total}
									icon={<LibraryBooksOutlinedIcon />}
									color="primary"
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<KpiStat
									label="No tasks"
									value={kpis.withNoTasks}
									icon={<PlaylistRemoveOutlinedIcon />}
									color="secondary"
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<KpiStat
									label="Total suggestions"
									value={totalSuggestions}
									icon={<RateReviewOutlinedIcon />}
									color="info"
								/>
							</Grid>
							<Grid item xs={12} sm={6} md={3}>
								<KpiStat
									label="Unverified suggestions"
									value={totalUnverified}
									icon={<ReportProblemOutlinedIcon />}
									color="warning"
								/>
							</Grid>
						</Grid>

						<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
							<Typography fontWeight={800} sx={{ mb: 1 }}>
								Procedures by group (top 12)
							</Typography>
							<ReactECharts
								style={{ height: 280 }}
								option={optByGroup}
								notMerge
							/>
						</Paper>

						<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
							<Typography fontWeight={800} sx={{ mb: 1 }}>
								Suggestions by status
							</Typography>
							<ReactECharts
								style={{ height: 280 }}
								option={optSuggestionsPie}
								notMerge
							/>
						</Paper>
					</Stack>
				</Grid>

				{/* RIGHT — fixed height + scroll */}
				<Grid item xs={12} md={7} lg={6} sx={{ mt: 2 }}>
					<Paper
						variant="outlined"
						sx={{
							borderRadius: 2,
							height: { xs: 480, md: "53vh" },
							display: "flex",
							flexDirection: "column",
							minHeight: 360,
						}}
					>
						{/* Tabs + Sorting aligned */}
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems="center"
							justifyContent="space-between"
							spacing={1}
							sx={{
								px: 1,
								borderBottom: (t) => `1px solid ${t.palette.divider}`,
								minHeight: 56,
							}}
						>
							<Tabs
								value={tab}
								onChange={(_, v) => setTab(v)}
								sx={{ minHeight: 44, "& .MuiTab-root": { minHeight: 44 } }}
							>
								<Tab label={`All (${filtered.length})`} />
								<Tab
									label="Recently updated"
									onClick={() => {
										setOnlyQuarter(true);
										setTab(1);
									}}
								/>
							</Tabs>

							<Stack direction="row" spacing={1} sx={{ py: 1 }}>
								<FormControl size="small" sx={{ minWidth: 160 }}>
									<InputLabel id="sort-by-label">Sort by</InputLabel>
									<Select
										labelId="sort-by-label"
										label="Sort by"
										value={sortBy}
										onChange={(e) => setSortBy(e.target.value as SortBy)}
									>
										<MenuItem value="name">Name</MenuItem>
										<MenuItem value="updated">Updated</MenuItem>
										<MenuItem value="suggestions">Suggestions</MenuItem>
										<MenuItem value="unverified">Unverified</MenuItem>
										<MenuItem value="tasks">Tasks</MenuItem>
										<MenuItem value="group">Group</MenuItem>
										<MenuItem value="status">Status</MenuItem>
										<MenuItem value="id">ID</MenuItem>
									</Select>
								</FormControl>
								<Tooltip title={sortDir === "asc" ? "Ascending" : "Descending"}>
									<IconButton
										onClick={() =>
											setSortDir((d) => (d === "asc" ? "desc" : "asc"))
										}
										sx={{
											border: (t) => `1px solid ${t.palette.divider}`,
											borderRadius: 2,
											width: 40,
											height: 40,
										}}
									>
										{sortDir === "asc" ? (
											<NorthIcon fontSize="small" />
										) : (
											<SouthIcon fontSize="small" />
										)}
									</IconButton>
								</Tooltip>
							</Stack>
						</Stack>

						{/* Scrollable list */}
						<Box sx={{ p: 1.5, flex: 1, minHeight: 0, overflowY: "auto" }}>
							<Grid container spacing={1.25} alignItems="stretch">
								{sorted.map((p) => {
									const groupName = groupNameById.get(p.GroupId) ?? "Ungrouped";
									const updatedAgo = p.Updated
										? dayjs(p.Updated).fromNow()
										: "—";
									const recent = isWithinDays(p.Updated, 7);

									const sugCounts = suggestionCountsByProcedure.get(p.Id);
									const sugTotal = sugCounts?.total ?? 0;
									const sugUnverified = sugCounts?.unverified ?? 0;

									return (
										<Grid item xs={12} sm={6} key={p.Id}>
											<Paper
												variant="outlined"
												sx={{
													p: 1.25,
													borderRadius: 2,
													display: "grid",
													gap: 0.5,
													height: "100%",
													transition:
														"box-shadow .15s ease, transform .15s ease",
													"&:hover": {
														boxShadow: 3,
														transform: "translateY(-1px)",
													},
													borderColor:
														sugUnverified > 0
															? theme.palette.warning.light
															: undefined,
												}}
											>
												{/* Title row (perfectly clipped) */}
												<Stack
													direction="row"
													alignItems="center"
													spacing={1}
													sx={{ minWidth: 0 }}
												>
													<SATOSLink
														to="AccessProcedure"
														params={[p.Id]}
														className="accordion-item-link"
														style={{
															display: "grid",
															gridTemplateColumns: "1fr auto", // text | arrow
															alignItems: "center",
															columnGap: 8,
															textDecoration: "none",
															color: "inherit",
															minWidth: 0, // allow shrink
															width: "100%", // take available width only
														}}
													>
														<Typography
															variant="subtitle2"
															fontWeight={800}
															title={p.Name}
															sx={{
																minWidth: 0,
																overflow: "hidden",
																whiteSpace: "nowrap",
																textOverflow: "ellipsis",
															}}
														>
															{p.Name}
														</Typography>

														<ArrowForwardIcon
															fontSize="small"
															sx={{ justifySelf: "end", flexShrink: 0 }}
															aria-hidden
														/>
													</SATOSLink>

													<Tooltip title="Open in new window">
														<IconButton
															size="small"
															component={SATOSLink as any}
															to="AccessProcedure"
															params={[p.Id]}
															target="_blank"
															aria-label="open new"
															sx={{ flexShrink: 0 }}
														>
															<OpenInNewIcon fontSize="inherit" />
														</IconButton>
													</Tooltip>
												</Stack>

												{/* Meta row (chips align) */}
												{/* Meta row (chips align) */}
												<Stack
													direction="row"
													spacing={0.75}
													alignItems="center"
													flexWrap="wrap"
												>
													<Chip
														size="small"
														variant="outlined"
														label={`ID ${p.Id}`}
													/>
													<Chip
														size="small"
														color="primary"
														variant="outlined"
														label={groupName}
													/>
													<Chip
														size="small"
														variant={recent ? "filled" : "outlined"}
														color={recent ? "success" : "default"}
														label={
															p.Updated ? `Updated ${updatedAgo}` : "No updates"
														}
													/>
													<Chip
														size="small"
														variant="outlined"
														label={`Status: ${statusLabel(p.ProcedureStatusNum)}`}
													/>
													<Chip
														size="small"
														variant="outlined"
														label={`Tasks: ${p.TotalTasks ?? 0}`}
													/>

													{/* 🔗 Suggestions chip now links to the correct Procedure Updates page */}
													{sugTotal > 0 ? (
														(() => {
															const target = findBestSuggestionTarget(p.Id);
															const deepLink = target
																? `/procedures/procedureUpdates/${target.tab}?id=${p.Id}&checklistId=${target.checklistId}` +
																	(target.suggestionId
																		? `&suggestionId=${target.suggestionId}`
																		: "") +
																	(target.stepId
																		? `&stepId=${target.stepId}`
																		: "")
																: undefined;

															const label =
																sugUnverified > 0
																	? `${sugTotal} suggestions • ${sugUnverified} unverified`
																	: `${sugTotal} suggestions`;

															const chip = (
																<Chip
																	size="small"
																	color={
																		sugUnverified > 0 ? "warning" : "default"
																	}
																	variant="outlined"
																	icon={
																		<ReportProblemOutlinedIcon fontSize="small" />
																	}
																	label={label}
																	{...(deepLink
																		? {
																				component: RouterLink as any,
																				to: deepLink,
																				target: "_blank",
																				rel: "noopener noreferrer",
																				clickable: true,
																				onClick: (e: React.MouseEvent) => {
																					e.stopPropagation();
																					if (target)
																						seedSuggestionSelection(
																							target,
																							p.Id,
																						);
																				},
																			}
																		: {})}
																/>
															);

															return deepLink ? (
																<Tooltip title="Open related suggestions (new tab)">
																	{chip}
																</Tooltip>
															) : (
																chip
															);
														})()
													) : (
														<Chip
															size="small"
															variant="outlined"
															label="No suggestions"
														/>
													)}
												</Stack>

												{/* Footer row */}
												<Stack
													direction="row"
													spacing={1}
													alignItems="center"
													justifyContent="space-between"
												>
													<Typography variant="caption" color="text.secondary">
														Last modified: {fmtDate(p.Updated)}
													</Typography>
													{p.WriteProtected && (
														<Chip
															size="small"
															color="warning"
															variant="outlined"
															label="Write-protected"
														/>
													)}
												</Stack>
											</Paper>
										</Grid>
									);
								})}
							</Grid>

							{sorted.length === 0 && (
								<Box
									sx={{ textAlign: "center", py: 6, color: "text.secondary" }}
								>
									<Typography fontWeight={800}>No results</Typography>
									<Typography variant="body2">
										Try clearing filters or searching something else.
									</Typography>
								</Box>
							)}
						</Box>
					</Paper>
				</Grid>
			</Grid>
			<Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
				<Stack
					direction="row"
					alignItems="center"
					justifyContent="space-between"
					sx={{ mb: 1 }}
				>
					<Typography fontWeight={800}>
						Updates over time (last {rangeLabel})
					</Typography>
					<Stack direction="row" spacing={1}>
						{RANGE_CHOICES.map((d) => (
							<Chip
								key={d}
								size="small"
								label={RANGE_LABELS[d]}
								variant={rangeDays === d ? "filled" : "outlined"}
								color={rangeDays === d ? "primary" : "default"}
								onClick={() => setRangeDays(d)}
								sx={{ borderRadius: 2 }}
							/>
						))}
					</Stack>
				</Stack>
				<ReactECharts style={{ height }} option={optByDay} notMerge />
			</Paper>
		</Stack>
	);
};

export default ProceduresOverview;
